import { useFilterPersistence } from 'components/tables/list-table/useFilterPersistence';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { DashboardForm, timeRangeOptions } from 'types/dashboard';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { getDatesFromTimeRangeOption } from './finalized-hours/time';

/**
 * The period form the dashboard widgets read through `useFormContext<DashboardForm>()`. It is remembered
 * under the page's path, so the classic dashboard and Home, both at `/home`, share one period.
 */
export const useDashboardForm = () => {
    const { getCustomData, saveCustomData } = useFilterPersistence({ canRememberFilters: true });
    const persistedData = getCustomData<DashboardForm>();

    const defaultValues: DashboardForm = persistedData || {
        selectedTimeRange: timeRangeOptions.find((option) => option.value === 'today') || null,
        dateFrom: format(new Date(), DEFAULT_DATE_FORMAT),
        dateTo: format(new Date(), DEFAULT_DATE_FORMAT),
    };
    const methods = useForm<DashboardForm>({
        defaultValues,
    });
    const { control, setValue } = methods;

    const selectedTimeRange = useWatch({ control, name: 'selectedTimeRange' });
    const dateFrom = useWatch({ control, name: 'dateFrom' });
    const dateTo = useWatch({ control, name: 'dateTo' });
    const hoursTab = useWatch({ control, name: 'hoursTab' });

    useEffect(() => {
        if (selectedTimeRange?.value !== 'custom') {
            const { from, to } = getDatesFromTimeRangeOption(selectedTimeRange?.value);
            setValue('dateFrom', from);
            setValue('dateTo', to);
        }
    }, [selectedTimeRange, setValue]);

    useEffect(() => {
        if (selectedTimeRange && dateFrom && dateTo) {
            saveCustomData({
                selectedTimeRange,
                dateFrom,
                dateTo,
                hoursTab,
            });
        }
    }, [selectedTimeRange, dateFrom, dateTo, hoursTab, saveCustomData]);

    return methods;
};
